import * as React from 'react';
import {ExercisePreviewProps} from './ExercisePreview.type';
import {ExercisePreviewService} from './ExercisePreview.service';
import {
  ExercisePreviewContainer,
  ExercisePreviewHeader,
  ExerciseImageThumb,
  ExerciseInfo,
  ExerciseName,
  ExerciseDetailsGrid,
  ExerciseDetail,
  ExerciseActions,
  ExerciseActionButton,
  MoveButtons,
  ExerciseNotes,
} from './ExercisePreview.style';

/**
 * ExercisePreview Component
 * Displays a preview of an exercise with edit/delete actions
 *
 * Migration Notes:
 * - Previously used WorkoutRoutineContext for updateExerciseWorkoutPosition
 * - Now accepts onMoveUp and onMoveDown as props directly
 * - Requirements: 7.3 - Remove legacy context dependencies
 */
export const ExercisePreview = ({
  workoutExercise,
  workout,
  workoutGroup,
  workoutGroupIndex,
  workoutIndex,
  exerciseIndex,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
}: ExercisePreviewProps): JSX.Element => {
  const exerciseData =
    ExercisePreviewService.formatExerciseData(workoutExercise);
  const hasDetails = ExercisePreviewService.hasAnyDetails(exerciseData.details);

  const handleMoveUp = () => {
    if (onMoveUp) {
      onMoveUp();
    }
  };

  const handleMoveDown = () => {
    if (onMoveDown) {
      onMoveDown();
    }
  };

  const renderDetails = () => {
    const {series, reps, weight, time, restTime} = exerciseData.details;
    const details = [
      ExercisePreviewService.formatDetailText('Séries', series),
      ExercisePreviewService.formatDetailText('Reps', reps),
      ExercisePreviewService.formatDetailText('Carga', weight),
      ExercisePreviewService.formatDetailText('Tempo', time),
      ExercisePreviewService.formatDetailText('Descanso', restTime),
    ].filter(Boolean);

    return details.map((detail, index) => (
      <ExerciseDetail key={index}>
        <span>{detail}</span>
      </ExerciseDetail>
    ));
  };

  return (
    <ExercisePreviewContainer>
      <ExercisePreviewHeader>
        <MoveButtons>
          <i
            onClick={handleMoveUp}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleMoveUp()}
            className="far fa-long-arrow-up"
            title="Mover exercício para cima"
            role="button"
            tabIndex={0}
          />
          <i
            onClick={handleMoveDown}
            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleMoveDown()}
            className="far fa-long-arrow-down"
            title="Mover exercício para baixo"
            role="button"
            tabIndex={0}
          />
        </MoveButtons>

        <ExerciseImageThumb>
          {exerciseData.imageUrl ? (
            <img src={exerciseData.imageUrl} alt={exerciseData.name} />
          ) : (
            <i
              className="far fa-dumbbell"
              style={{fontSize: '1.5rem', color: '#999'}}
            />
          )}
        </ExerciseImageThumb>

        <ExerciseInfo>
          <ExerciseName>{exerciseData.name}</ExerciseName>
          {hasDetails && (
            <ExerciseDetailsGrid>{renderDetails()}</ExerciseDetailsGrid>
          )}
        </ExerciseInfo>

        <ExerciseActions>
          <ExerciseActionButton
            type="button"
            className="edit"
            onClick={onEdit}
            title="Editar exercício">
            <i className="far fa-edit" />
            Editar
          </ExerciseActionButton>

          <ExerciseActionButton
            type="button"
            className="delete"
            onClick={onDelete}
            title="Remover exercício">
            <i className="far fa-trash" />
            Apagar
          </ExerciseActionButton>
        </ExerciseActions>
      </ExercisePreviewHeader>

      {exerciseData.details.notes && (
        <ExerciseNotes>
          <strong>Observações:</strong>
          {exerciseData.details.notes}
        </ExerciseNotes>
      )}
    </ExercisePreviewContainer>
  );
};
